// src/components/social/SocialMap.jsx
// Carte sociale premium : recherche par ville (texte libre + a proximite), avatars emoji

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from '../../utils/leafletConfig';
import { useTheme } from '../../hooks/useTheme';
import * as socialApi from '../../utils/socialApi';

const KNOWN_CITIES = [
  { name: 'Douala', lat: 4.0511, lng: 9.7679 },
  { name: 'Yaoundé', lat: 3.8480, lng: 11.5021 },
  { name: 'Bafoussam', lat: 5.4737, lng: 10.4179 },
  { name: 'Bamenda', lat: 5.9597, lng: 10.1459 },
  { name: 'Garoua', lat: 9.3017, lng: 13.3921 },
  { name: 'Maroua', lat: 10.5956, lng: 14.3247 },
  { name: 'Ngaoundéré', lat: 7.3167, lng: 13.5833 },
  { name: 'Bertoua', lat: 4.5772, lng: 13.6846 },
  { name: 'Ebolowa', lat: 2.9167, lng: 11.1500 },
  { name: 'Kribi', lat: 2.9500, lng: 9.9000 },
  { name: 'Limbe', lat: 4.0225, lng: 9.2136 },
  { name: 'Buea', lat: 4.1560, lng: 9.2870 },
  { name: 'Dschang', lat: 5.4500, lng: 10.0500 },
  { name: 'Edéa', lat: 3.8000, lng: 10.1333 },
  { name: 'Kumba', lat: 4.6363, lng: 9.4470 },
  { name: 'Nkongsamba', lat: 4.9547, lng: 9.9401 },
  { name: 'Foumban', lat: 5.7263, lng: 10.9166 },
  { name: 'Bafang', lat: 5.1667, lng: 10.1833 },
  { name: 'Mbouda', lat: 5.6333, lng: 10.2500 },
  { name: 'Sangmélima', lat: 2.9333, lng: 11.9833 },
];

const GENDER_EMOJI = { homme: '👨', femme: '👩', autre: '🧑' };

function normalizeCityName(s) {
  return s.trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function findCityByName(input) {
  const target = normalizeCityName(input);
  return KNOWN_CITIES.find((c) => normalizeCityName(c.name) === target) || null;
}

function extractErrorMessage(err) {
  if (!err) return null;
  return err?.response?.data?.error || err?.data?.error || err?.error || err?.message || null;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findNearestCity(lat, lng) {
  let best = KNOWN_CITIES[0];
  let bestDist = Infinity;
  KNOWN_CITIES.forEach((c) => {
    const d = haversineKm(lat, lng, c.lat, c.lng);
    if (d < bestDist) { bestDist = d; best = c; }
  });
  return best;
}

// Ecarte legerement les avatars qui partagent exactement les memes coordonnees
// (meme quartier) pour qu'ils ne se superposent pas et masquent les autres.
// Deterministe (base sur user_id) donc la position d'un utilisateur ne bouge pas au rechargement.
function jitterPosition(userId, lat, lng) {
  const seed = parseInt(userId, 10) || 0;
  const angleDeg = (seed * 137.508) % 360; // angle d'or, repartition homogene
  const radius = 0.0012 + (seed % 7) * 0.0003; // ~130m a ~350m selon l'utilisateur
  const rad = (angleDeg * Math.PI) / 180;
  return [lat + radius * Math.cos(rad), lng + radius * Math.sin(rad)];
}

function FitToUsers({ users, trigger, fallbackCenter }) {
  const map = useMap();
  useEffect(() => {
    const points = users.filter((u) => u.latitude && u.longitude).map((u) => [parseFloat(u.latitude), parseFloat(u.longitude)]);
    if (points.length === 0) {
      map.flyTo(fallbackCenter, 12, { duration: 0.8 });
      return;
    }
    if (points.length === 1) {
      map.flyTo(points[0], 14, { duration: 0.8 });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 14, duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

const SocialMap = ({ city, userId, isAuthenticated, onRequireLogin }) => {
  const { theme } = useTheme();
  const [selectedCity, setSelectedCity] = useState(city || 'Douala');
  const [cityInput, setCityInput] = useState(city || 'Douala');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [inviteSent, setInviteSent] = useState({});
  const [searchingNearby, setSearchingNearby] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [fitTrigger, setFitTrigger] = useState(0);
  const initialCityRef = useRef(city);
  const cityInputDebounce = useRef(null);

  useEffect(() => {
    if (city && city !== initialCityRef.current) {
      initialCityRef.current = city;
      setSelectedCity(city);
      setCityInput(city);
    }
  }, [city]);

  const loadUsers = useCallback(async (targetCity) => {
    if (!targetCity) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await socialApi.getMapUsers(targetCity, userId || 0);
      setUsers(res && res.users ? res.users : []);
      if (!res) setLoadError('Reponse vide du serveur - verifie la connexion API.');
    } catch (err) {
      setUsers([]);
      setLoadError(extractErrorMessage(err) || 'Erreur de chargement inconnue.');
    } finally {
      setLoading(false);
      setFitTrigger((t) => t + 1);
    }
  }, [userId]);

  useEffect(() => {
    loadUsers(selectedCity);
    const interval = setInterval(() => loadUsers(selectedCity), 30000);
    return () => clearInterval(interval);
  }, [selectedCity, loadUsers]);

  const handleCityInputChange = (value) => {
    setCityInput(value);
    setStatusMessage(null);
    if (cityInputDebounce.current) clearTimeout(cityInputDebounce.current);
    cityInputDebounce.current = setTimeout(() => {
      const match = findCityByName(value);
      if (match) {
        setSelectedCity(match.name);
      } else if (value.trim().length >= 2) {
        setStatusMessage({ text: `Ville "${value}" non reconnue. Essaie: Douala, Yaoundé, Bafoussam...`, tone: 'warning' });
      }
    }, 400);
  };

  const handleSearchNearby = () => {
    if (!navigator.geolocation) {
      setStatusMessage({ text: 'La geolocalisation n\'est pas disponible sur cet appareil.', tone: 'warning' });
      return;
    }
    setSearchingNearby(true);
    setStatusMessage(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nearest = findNearestCity(pos.coords.latitude, pos.coords.longitude);
        setSelectedCity(nearest.name);
        setCityInput(nearest.name);
        setStatusMessage({ text: `📍 Recherche des personnes a ${nearest.name}...`, tone: 'info' });
        setSearchingNearby(false);
      },
      () => {
        setSearchingNearby(false);
        setStatusMessage({ text: 'Impossible d\'obtenir ta position. Verifie les autorisations de localisation.', tone: 'warning' });
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleInvite = async (targetUserId) => {
    if (!isAuthenticated) {
      onRequireLogin();
      return;
    }
    try {
      await socialApi.sendFriendRequest(userId, targetUserId);
      setInviteSent((prev) => ({ ...prev, [targetUserId]: true }));
    } catch (err) {
      // silencieux, le bouton reste cliquable
    }
  };

  const createAvatarIcon = (user) => {
    const onlineDot = user.is_online
      ? `<span style="position:absolute; bottom:-1px; right:-1px; width:12px; height:12px; border-radius:50%; background:#48BB78; border:2px solid #FFFFFF; box-shadow:0 0 0 2px rgba(72,187,120,0.3);"></span>`
      : '';
    const emoji = GENDER_EMOJI[user.gender] || '🧑';
    const innerContent = user.avatar
      ? `<img src="${user.avatar}" style="width:100%; height:100%; border-radius:50%; object-fit:cover; display:block;" />`
      : `<div style="
          width:100%; height:100%; border-radius:50%;
          background: ${theme.mode === 'light' ? '#F3F4F6' : '#1A1F2E'};
          display:flex; align-items:center; justify-content:center; font-size:20px;
        ">${emoji}</div>`;

    return L.divIcon({
      className: 'social-avatar-marker',
      html: `
        <div style="position:relative; width:48px; height:48px;">
          <div style="
            width:48px; height:48px; border-radius:50%; padding:2px;
            background: linear-gradient(135deg, ${theme.gold}, #B8860B);
            box-shadow:0 4px 12px rgba(0,0,0,0.35);
          ">
            <div style="width:100%; height:100%; border-radius:50%; overflow:hidden; border:2px solid ${theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A'};">
              ${innerContent}
            </div>
          </div>
          ${onlineDot}
        </div>
      `,
      iconSize: [48, 48],
      iconAnchor: [24, 24],
    });
  };

  const fallbackCenter = (KNOWN_CITIES.find((c) => c.name === selectedCity) || KNOWN_CITIES[0]);
  const onlineCount = users.filter((u) => u.is_online).length;
  const usersWithJitter = users.map((u) => {
    const [jLat, jLng] = jitterPosition(u.user_id, parseFloat(u.latitude), parseFloat(u.longitude));
    return { ...u, jLat, jLng };
  });

  const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '0' },
    toolbar: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' },
    cityInput: {
      flex: 1, minWidth: '140px', padding: '10px 14px', backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`, borderRadius: '12px', color: theme.textPrimary,
      fontSize: '13px', outline: 'none',
    },
    nearbyBtn: {
      display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 16px',
      background: `linear-gradient(135deg, ${theme.gold}, #B8860B)`,
      border: 'none', borderRadius: '12px', color: '#FFFFFF', fontSize: '13px', fontWeight: '700',
      cursor: 'pointer', boxShadow: '0 4px 14px rgba(184,134,11,0.35)', flexShrink: 0, whiteSpace: 'nowrap',
    },
    countLine: { fontSize: '12px', color: theme.textSecondary, marginBottom: '10px' },
    countStrong: { color: theme.textPrimary, fontWeight: '700' },
    statusBanner: (tone) => ({
      padding: '8px 12px', borderRadius: '9px', fontSize: '12px', textAlign: 'center', marginBottom: '10px',
      backgroundColor: tone === 'warning' ? 'rgba(237,137,54,0.12)' : 'rgba(255,215,0,0.10)',
      border: `1px solid ${tone === 'warning' ? 'rgba(237,137,54,0.35)' : 'rgba(255,215,0,0.3)'}`,
      color: tone === 'warning' ? '#DD6B20' : theme.gold,
    }),
    errorBanner: {
      padding: '10px 12px', borderRadius: '9px', fontSize: '12px', textAlign: 'center', marginBottom: '10px',
      backgroundColor: 'rgba(245,101,101,0.10)', border: '1px solid rgba(245,101,101,0.35)', color: '#F56565',
    },
    mapFrame: {
      position: 'relative', borderRadius: '20px', padding: '3px',
      background: `linear-gradient(135deg, ${theme.gold}, rgba(184,134,11,0.3), ${theme.border})`,
      boxShadow: theme.mode === 'light' ? '0 10px 30px rgba(0,0,0,0.12)' : '0 10px 30px rgba(0,0,0,0.5)',
    },
    mapCard: { position: 'relative', height: '60vh', minHeight: '380px', borderRadius: '18px', overflow: 'hidden', backgroundColor: theme.bgCard },
    mapTopBar: {
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', background: 'linear-gradient(180deg, rgba(10,14,26,0.75), transparent)',
      pointerEvents: 'none',
    },
    mapTopBarText: { fontSize: '12px', fontWeight: '700', color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
    mapTopBarOnline: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
    onlineDotSmall: { width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#48BB78', flexShrink: 0 },
    popupWrap: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' },
    popupName: { fontWeight: '700', fontSize: '14px' },
    popupLoc: { fontSize: '12px', color: '#666' },
    popupBtn: { marginTop: '6px', padding: '7px', backgroundColor: theme.gold, color: '#FFFFFF', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', width: '100%' },
    popupBtnSent: { marginTop: '6px', padding: '7px', backgroundColor: '#48BB78', color: '#FFFFFF', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', width: '100%', textAlign: 'center' },
    listCard: { marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '0', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '16px', overflow: 'hidden' },
    listTitle: { fontSize: '13px', fontWeight: '700', color: theme.textPrimary, padding: '14px 14px 8px' },
    listScroll: { display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '320px', overflowY: 'auto', padding: '0 14px 14px' },
    listEmpty: { fontSize: '12px', color: theme.textSecondary, textAlign: 'center', padding: '12px' },
    listRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '10px' },
    listAvatarWrap: { position: 'relative', width: '38px', height: '38px', flexShrink: 0 },
    listAvatarImg: { width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' },
    listAvatarEmoji: { width: '38px', height: '38px', borderRadius: '50%', backgroundColor: theme.mode === 'light' ? '#F3F4F6' : '#1A1F2E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px' },
    listOnlineDot: { position: 'absolute', bottom: '-1px', right: '-1px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#48BB78', border: `2px solid ${theme.bgCard}` },
    listInfo: { flex: 1, minWidth: 0 },
    listName: { fontSize: '13px', fontWeight: '600', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    listLoc: { fontSize: '11px', color: theme.textSecondary },
    listInviteBtn: { padding: '7px 12px', fontSize: '11px', fontWeight: '700', borderRadius: '8px', border: 'none', backgroundColor: theme.gold, color: '#FFFFFF', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    listSentBadge: { fontSize: '11px', fontWeight: '700', color: '#48BB78', flexShrink: 0 },
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <input
          style={styles.cityInput}
          type="text"
          list="social-known-cities"
          value={cityInput}
          onChange={(e) => handleCityInputChange(e.target.value)}
          placeholder="Rechercher une ville (Douala, Yaoundé...)"
        />
        <datalist id="social-known-cities">
          {KNOWN_CITIES.map((c) => <option key={c.name} value={c.name} />)}
        </datalist>
        <button style={styles.nearbyBtn} onClick={handleSearchNearby} disabled={searchingNearby}>
          🔍 {searchingNearby ? 'Recherche...' : 'A proximite'}
        </button>
      </div>

      <div style={styles.countLine}>
        {loading ? 'Chargement...' : (
          <>
            <span style={styles.countStrong}>{users.length}</span> personne{users.length > 1 ? 's' : ''} a <span style={styles.countStrong}>{selectedCity}</span>
          </>
        )}
      </div>

      {loadError && <div style={styles.errorBanner}>⚠️ Erreur de chargement de la carte : {loadError}</div>}
      {statusMessage && <div style={styles.statusBanner(statusMessage.tone)}>{statusMessage.text}</div>}

      <div style={styles.mapFrame}>
        <div style={styles.mapCard}>
          <div style={styles.mapTopBar}>
            <span style={styles.mapTopBarText}>✨ Fynho Social — {selectedCity}</span>
            <span style={styles.mapTopBarOnline}><span style={styles.onlineDotSmall} /> {onlineCount} en ligne</span>
          </div>
          <MapContainer center={[fallbackCenter.lat, fallbackCenter.lng]} zoom={13} style={{ width: '100%', height: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitToUsers users={usersWithJitter.map((u) => ({ latitude: u.jLat, longitude: u.jLng }))} trigger={fitTrigger} fallbackCenter={[fallbackCenter.lat, fallbackCenter.lng]} />
            {usersWithJitter.map((user) => (
              <Marker key={user.user_id} position={[user.jLat, user.jLng]} icon={createAvatarIcon(user)}>
                <Popup>
                  <div style={styles.popupWrap}>
                    <div style={styles.popupName}>
                      {user.username} {GENDER_EMOJI[user.gender] || ''}
                    </div>
                    <div style={styles.popupLoc}>📍 {user.district}</div>
                    <div style={styles.popupLoc}>{user.is_online ? '🟢 En ligne' : 'Hors ligne'}</div>
                    {inviteSent[user.user_id] ? (
                      <div style={styles.popupBtnSent}>✓ Invitation envoyee</div>
                    ) : (
                      <button style={styles.popupBtn} onClick={() => handleInvite(user.user_id)}>
                        {isAuthenticated ? 'Envoyer une invitation' : 'Se connecter pour inviter'}
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      <div style={styles.listCard}>
        <div style={styles.listTitle}>Personnes a {selectedCity}</div>
        <div style={styles.listScroll}>
          {loading && <div style={styles.listEmpty}>Chargement...</div>}
          {!loading && users.length === 0 && <div style={styles.listEmpty}>Aucune personne trouvee ici pour le moment.</div>}
          {!loading && [...users].sort((a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0)).map((user) => (
            <div key={user.user_id} style={styles.listRow}>
              <div style={styles.listAvatarWrap}>
                {user.avatar ? (
                  <img src={user.avatar} alt={user.username} style={styles.listAvatarImg} />
                ) : (
                  <div style={styles.listAvatarEmoji}>{GENDER_EMOJI[user.gender] || '🧑'}</div>
                )}
                {user.is_online && <span style={styles.listOnlineDot} />}
              </div>
              <div style={styles.listInfo}>
                <div style={styles.listName}>{user.username}</div>
                <div style={styles.listLoc}>{user.district} · {user.is_online ? '🟢 En ligne' : 'Hors ligne'}</div>
              </div>
              {inviteSent[user.user_id] ? (
                <span style={styles.listSentBadge}>✓ Envoyee</span>
              ) : (
                <button style={styles.listInviteBtn} onClick={() => handleInvite(user.user_id)}>
                  {isAuthenticated ? 'Inviter' : '🔒 Se connecter'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialMap;